import React, { useEffect, useState, useMemo } from "react";
import { eventsState } from "../../recoilStates";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Divider,
  Stack,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AdsClickOutlinedIcon from "@mui/icons-material/AdsClickOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import { useRecoilState } from "recoil";
import { getAdEvents } from "../../assets/firebaseFirestore";
import * as XLSX from "xlsx";

// ─── Excel Export ─────────────────────────────────────────────────────────────

/**
 * Applies a bold header style to a row in a worksheet.
 * @param {object} ws - openpyxl-style worksheet from XLSX.utils
 * @param {number} row - 0-based row index
 * @param {number} colCount - number of columns
 */
function styleHeaderRow(ws, row, colCount) {
  for (let c = 0; c < colCount; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: row, c });
    if (!ws[cellRef]) continue;
    ws[cellRef].s = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11, name: "Arial" },
      fill: { patternType: "solid", fgColor: { rgb: "E91E8C" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        bottom: { style: "thin", color: { rgb: "C4177A" } },
      },
    };
  }
}

function styleDataCell(ws, row, col, isEven) {
  const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
  if (!ws[cellRef]) return;
  ws[cellRef].s = {
    font: { sz: 10, name: "Arial" },
    fill: isEven
      ? { patternType: "solid", fgColor: { rgb: "FFF5FB" } }
      : { patternType: "solid", fgColor: { rgb: "FFFFFF" } },
    alignment: { vertical: "center" },
  };
}

function autoWidth(ws, data) {
  if (!data.length) return;
  const cols = Object.keys(data[0]);
  ws["!cols"] = cols.map((key) => {
    const maxLen = Math.max(
      key.length,
      ...data.map((row) => String(row[key] ?? "").length)
    );
    return { wch: Math.min(maxLen + 4, 50) };
  });
}

/**
 * Builds and downloads a multi-sheet .xlsx statistics report.
 * Uses SheetJS (xlsx) — pure client-side, no server needed.
 * Install: npm install xlsx  (or yarn add xlsx)
 */
function exportToExcel(stats, rawAds) {
  if (!stats) return;

  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: "Ad Statistics Report",
    Author: "Analytics Dashboard",
    CreatedDate: new Date(),
  };

  // ── Sheet 1: Summary ──────────────────────────────────────────────────────
  const summaryData = [
    { Metric: "Total Views", Value: stats.totalViews },
    { Metric: "Total Clicks", Value: stats.totalClicks },
    { Metric: "CTR (%)", Value: parseFloat(stats.ctr) },
    { Metric: "Active Ads", Value: stats.activeAds },
    { Metric: "Total Ads", Value: rawAds.length },
    { Metric: "Report Date", Value: new Date().toLocaleDateString("en-US") },
  ];
  const wsSummary = XLSX.utils.json_to_sheet(summaryData);
  wsSummary["!cols"] = [{ wch: 20 }, { wch: 20 }];
  styleHeaderRow(wsSummary, 0, 2);
  summaryData.forEach((_, i) => {
    styleDataCell(wsSummary, i + 1, 0, i % 2 === 0);
    styleDataCell(wsSummary, i + 1, 1, i % 2 === 0);
  });
  XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

  // ── Sheet 2: Advertisements ───────────────────────────────────────────────
  const adsData = stats.sortedAds.map((ad) => ({
    "Ad Title": ad.title,
    "Status": ad.active ? "Active" : "Inactive",
    "Media Type": ad.mediaType || "image",
    "Views": ad.views,
    "Clicks": ad.clicks,
    "CTR (%)": parseFloat(ad.ctr),
    "Placements": ad.placements.map((p) => PLACEMENT_LABELS[p.id] || p.label).join(", "),
  }));
  const wsAds = XLSX.utils.json_to_sheet(adsData);
  autoWidth(wsAds, adsData);
  styleHeaderRow(wsAds, 0, Object.keys(adsData[0] || {}).length);
  adsData.forEach((_, i) => {
    Object.keys(adsData[0] || {}).forEach((__, c) => {
      styleDataCell(wsAds, i + 1, c, i % 2 === 0);
    });
  });
  XLSX.utils.book_append_sheet(wb, wsAds, "Advertisements");

  // ── Sheet 3: Daily Activity ───────────────────────────────────────────────
  const dailyData = stats.sortedDays.map((day) => ({
    Date: day,
    Views: stats.byDay[day]?.views || 0,
    Clicks: stats.byDay[day]?.clicks || 0,
    "CTR (%)":
      (stats.byDay[day]?.views || 0) > 0
        ? parseFloat(
            (
              ((stats.byDay[day]?.clicks || 0) / stats.byDay[day].views) *
              100
            ).toFixed(2)
          )
        : 0,
  }));
  const wsDaily = XLSX.utils.json_to_sheet(dailyData);
  autoWidth(wsDaily, dailyData);
  styleHeaderRow(wsDaily, 0, 4);
  dailyData.forEach((_, i) => {
    [0, 1, 2, 3].forEach((c) => styleDataCell(wsDaily, i + 1, c, i % 2 === 0));
  });
  XLSX.utils.book_append_sheet(wb, wsDaily, "Daily Activity");

  // ── Sheet 4: Placements ───────────────────────────────────────────────────
  const totalPlacementViews = stats.topPlacements.reduce((s, [, c]) => s + c, 0);
  const placementData = stats.topPlacements.map(([pl, count]) => ({
    Placement: PLACEMENT_LABELS[pl] || pl,
    "Placement ID": pl,
    Views: count,
    "Share (%)": totalPlacementViews > 0
      ? parseFloat(((count / totalPlacementViews) * 100).toFixed(1))
      : 0,
  }));
  const wsPlacement = XLSX.utils.json_to_sheet(placementData);
  autoWidth(wsPlacement, placementData);
  styleHeaderRow(wsPlacement, 0, 4);
  placementData.forEach((_, i) => {
    [0, 1, 2, 3].forEach((c) => styleDataCell(wsPlacement, i + 1, c, i % 2 === 0));
  });
  XLSX.utils.book_append_sheet(wb, wsPlacement, "Placements");

  // ── Sheet 5: Geo / Regions ────────────────────────────────────────────────
  const totalGeoViews = stats.topGeo.reduce((s, [, g]) => s + g.views, 0);
  const geoData = stats.topGeo.map(([, g]) => ({
    City: g.city || "",
    State: g.state || "",
    Country: g.country || "",
    Views: g.views,
    "Share (%)": totalGeoViews > 0
      ? parseFloat(((g.views / totalGeoViews) * 100).toFixed(1))
      : 0,
  }));
  const wsGeo = XLSX.utils.json_to_sheet(geoData);
  autoWidth(wsGeo, geoData);
  styleHeaderRow(wsGeo, 0, 3);
  geoData.forEach((_, i) => {
    [0, 1, 2].forEach((c) => styleDataCell(wsGeo, i + 1, c, i % 2 === 0));
  });
  XLSX.utils.book_append_sheet(wb, wsGeo, "Regions");

  // ── Sheet 6: Raw Events ───────────────────────────────────────────────────
  const rawEventsData = [];
  for (const ad of rawAds) {
    for (const ev of ad.events || []) {
      rawEventsData.push({
        "Ad ID": ad.id,
        "Ad Title": ad.title || "Untitled",
        "Event Type": ev.type,
        "Placement": PLACEMENT_LABELS[ev.placement] || ev.placement || "",
        "Date": ev.date ? ev.date.slice(0, 10) : "",
        "Time": ev.date ? ev.date.slice(11, 19) : "",
        "Country": ev.region?.country || "",
        "State": ev.region?.state || "",
        "City": ev.region?.city || "",
      });
    }
  }
  const wsRaw = XLSX.utils.json_to_sheet(rawEventsData);
  autoWidth(wsRaw, rawEventsData.slice(0, 20)); // sample for width calc
  styleHeaderRow(wsRaw, 0, Object.keys(rawEventsData[0] || {}).length);
  rawEventsData.forEach((_, i) => {
    Object.keys(rawEventsData[0] || {}).forEach((__, c) => {
      styleDataCell(wsRaw, i + 1, c, i % 2 === 0);
    });
  });
  XLSX.utils.book_append_sheet(wb, wsRaw, "Raw Events");

  // ── Download ──────────────────────────────────────────────────────────────
  const dateStr = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `ad-statistics-${dateStr}.xlsx`, { bookType: "xlsx", cellStyles: true });
}

// ─── Theme ────────────────────────────────────────────────────────────────────

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#e91e8c" },
    info: { main: "#1A73E8" },
    success: { main: "#4CAF50" },
    warning: { main: "#fb8c00" },
    error: { main: "#f44335" },
    background: { default: "#f0f2f5", paper: "#ffffff" },
    text: { primary: "#344767", secondary: "#7b809a" },
  },
  typography: {
    fontFamily: "'DM Sans', sans-serif",
    h4: { fontFamily: "'Sora', sans-serif", fontWeight: 700 },
    h5: { fontFamily: "'Sora', sans-serif", fontWeight: 700 },
    h6: { fontFamily: "'Sora', sans-serif", fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 24px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 700,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 13,
          letterSpacing: 0.3,
        },
        containedPrimary: {
          background: "linear-gradient(195deg, #f06292, #e91e8c)",
          boxShadow: "0 3px 8px rgba(233,30,140,0.35)",
          "&:hover": { boxShadow: "0 6px 16px rgba(233,30,140,0.4)" },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, height: 6, backgroundColor: "#f0f2f5" },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontFamily: "'Sora', sans-serif",
          fontWeight: 600,
          fontSize: 14,
          minHeight: 44,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 700,
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          color: "#7b809a",
          borderBottom: "1px solid #f0f2f5",
          padding: "12px 16px",
          backgroundColor: "#fafbfc",
        },
        body: {
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 14,
          borderBottom: "1px solid #f8f9fa",
          padding: "14px 16px",
          color: "#344767",
        },
      },
    },
  },
});

// ─── Constants ────────────────────────────────────────────────────────────────

const PLACEMENT_LABELS = {
  Option1: "Leaderboard",
  Option2: "In-Content (beginning)",
  Option3: "In-Content (mid)",
  Option4: "Footer Banner",
};

const PLACEMENT_COLORS = {
  Option1: "#1A73E8",
  Option2: "#e91e8c",
  Option3: "#fb8c00",
  Option4: "#4CAF50",
};

// ─── Data Parsing ─────────────────────────────────────────────────────────────

function parseAdEvents(ads) {
  if (!ads || !ads.length) return null;

  let totalViews = 0;
  let totalClicks = 0;
  const byPlacement = {};
  const byGeo = {};
  const byDay = {};
  const adStats = [];

  for (const ad of ads) {
    const events = ad.events || [];
    let adViews = 0;
    let adClicks = 0;

    for (const ev of events) {
      // By day
      const day = ev.date ? ev.date.slice(0, 10) : "unknown";
      if (!byDay[day]) byDay[day] = { views: 0, clicks: 0 };

      // By placement
      const pl = ev.placement || "unknown";
      if (!byPlacement[pl]) byPlacement[pl] = 0;

      // By geo — build a "City, State, Country" key
      const geoCity = ev.region?.city || "";
      const geoState = ev.region?.state || "";
      const geoCountry = ev.region?.country || "";
      const geoParts = [geoCity, geoState, geoCountry].filter(Boolean);
      const geo = geoParts.length ? geoParts.join(", ") : "Unknown";
      if (!byGeo[geo]) byGeo[geo] = { views: 0, city: geoCity, state: geoState, country: geoCountry };


      if (ev.type === "view") {
        totalViews++;
        adViews++;
        byDay[day].views++;
        byPlacement[pl]++;
        byGeo[geo].views++;
      } else if (ev.type === "click") {
        totalClicks++;
        adClicks++;
        byDay[day].clicks++;
      }
    }

    adStats.push({
      id: ad.id,
      title: ad.title || "Untitled",
      views: adViews,
      clicks: adClicks,
      ctr: adViews > 0 ? ((adClicks / adViews) * 100).toFixed(1) : "0.0",
      active: ad.active,
      mediaType: ad.mediaType,
      placements: ad.placement || [],
    });
  }

  const sortedDays = Object.keys(byDay).sort();
  const activeAds = ads.filter((a) => a.active).length;
  const ctr = totalViews > 0 ? ((totalClicks / totalViews) * 100).toFixed(1) : "0.0";

  // Top geo sorted — each entry: [geoKey, { views, city, state, country }]
  const topGeo = Object.entries(byGeo)
    .sort((a, b) => b[1].views - a[1].views)
    .slice(0, 5);

  // Top placements sorted
  const topPlacements = Object.entries(byPlacement).sort((a, b) => b[1] - a[1]);

  // Ads sorted by views
  const sortedAds = [...adStats].sort((a, b) => b.views - a.views);

  return {
    totalViews,
    totalClicks,
    ctr,
    activeAds,
    byDay,
    sortedDays,
    topGeo,
    topPlacements,
    sortedAds,
  };
}

// ─── SparkLine ────────────────────────────────────────────────────────────────

const SparkLine = ({ data, color }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const H = 40;
  const W = 100;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * W;
      const y = H - ((v - min) / range) * (H - 6) - 3;
      return `${x},${y}`;
    })
    .join(" ");
  const peakIdx = data.indexOf(Math.max(...data));
  return (
    <Box sx={{ width: "100%", height: 44 }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: "100%" }}
      >
        <defs>
          <linearGradient id={`sg${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polyline
          points={`0,${H} ${pts} ${W},${H}`}
          fill={`url(#sg${color.replace("#", "")})`}
          stroke="none"
        />
        <polyline
          points={pts}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {data.map((v, i) => {
          const x = (i / (data.length - 1)) * W;
          const y = H - ((v - min) / range) * (H - 6) - 3;
          return i === peakIdx ? (
            <circle key={i} cx={x} cy={y} r="2.5" fill={color} />
          ) : null;
        })}
      </svg>
    </Box>
  );
};

// ─── Mini Bar Chart ───────────────────────────────────────────────────────────

const MiniBarChart = ({ data, labels, color, height = 80 }) => {
  if (!data || !data.length) return null;
  const max = Math.max(...data) || 1;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, height }}>
      {data.map((v, i) => (
        <Box key={i} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
          <Box
            sx={{
              width: "100%",
              height: `${(v / max) * (height - 16)}px`,
              bgcolor: alpha(color, 0.15 + (v / max) * 0.65),
              borderRadius: "3px 3px 0 0",
              transition: "height 0.4s ease",
            }}
          />
          {labels && (
            <Typography variant="caption" sx={{ fontSize: 9, color: "text.secondary" }}>
              {labels[i]}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

// ─── Area Chart ───────────────────────────────────────────────────────────────

const AreaChart = ({ data1, data2, labels, color1, color2, height = 160 }) => {
  if (!data1 || data1.length < 2) {
    return (
      <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="caption" color="text.secondary">
          Недостаточно данных для графика
        </Typography>
      </Box>
    );
  }

  const allVals = [...data1, ...(data2 || [])];
  const max = Math.max(...allVals) || 1;
  const W = 400;
  const H = height;

  const toPoint = (arr, i) => {
    const x = (i / (arr.length - 1)) * W;
    const y = H - (arr[i] / max) * (H - 12) - 6;
    return `${x},${y}`;
  };

  const pts1 = data1.map((_, i) => toPoint(data1, i)).join(" ");
  const pts2 = data2 ? data2.map((_, i) => toPoint(data2, i)).join(" ") : null;

  return (
    <Box>
      <Box sx={{ width: "100%", position: "relative" }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height }}
        >
          <defs>
            <linearGradient id="ag1" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color1} stopOpacity="0.2" />
              <stop offset="100%" stopColor={color1} stopOpacity="0" />
            </linearGradient>
            {color2 && (
              <linearGradient id="ag2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color2} stopOpacity="0.15" />
                <stop offset="100%" stopColor={color2} stopOpacity="0" />
              </linearGradient>
            )}
          </defs>
          {[0.25, 0.5, 0.75].map((f, i) => (
            <line
              key={i}
              x1={0}
              y1={H * f}
              x2={W}
              y2={H * f}
              stroke="#f0f2f5"
              strokeWidth="1"
              strokeDasharray="4 4"
            />
          ))}
          {pts2 && (
            <>
              <polyline points={`0,${H} ${pts2} ${W},${H}`} fill="url(#ag2)" stroke="none" />
              <polyline
                points={pts2}
                fill="none"
                stroke={color2}
                strokeWidth="1.5"
                strokeLinejoin="round"
                strokeLinecap="round"
                strokeDasharray="5 3"
              />
            </>
          )}
          <polyline points={`0,${H} ${pts1} ${W},${H}`} fill="url(#ag1)" stroke="none" />
          <polyline
            points={pts1}
            fill="none"
            stroke={color1}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {data1.map((v, i) => {
            const [x, y] = toPoint(data1, i).split(",");
            return i === data1.indexOf(Math.max(...data1)) ? (
              <circle key={i} cx={x} cy={y} r="3" fill={color1} stroke="#fff" strokeWidth="1.5" />
            ) : null;
          })}
        </svg>
      </Box>
      {labels && (
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5, px: 0.25 }}>
          {labels.map((l, i) => (
            <Typography key={i} variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
              {l}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Empty State ──────────────────────────────────────────────────────────────

const EmptyState = ({ label = "Нет данных" }) => (
  <Box
    sx={{
      py: 4,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 1,
      color: "text.secondary",
    }}
  >
    <Typography variant="body2">{label}</Typography>
  </Box>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Analytics() {
  const [events, setEvents] = useRecoilState(eventsState);
  const [loading, setLoading] = useState(true);
  const [chartMode, setChartMode] = useState("views");
  const [exporting, setExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const handleExportClick = (e) => setExportMenuAnchor(e.currentTarget);
  const handleExportClose = () => setExportMenuAnchor(null);

  const handleExport = async (type) => {
    handleExportClose();
    if (!stats) return;
    setExporting(true);
    try {
      if (type === "full") {
        exportToExcel(stats, events);
      } else if (type === "ads") {
        const wb = XLSX.utils.book_new();
        const adsData = stats.sortedAds.map((ad) => ({
          "Ad Title": ad.title,
          "Status": ad.active ? "Active" : "Inactive",
          "Media Type": ad.mediaType || "image",
          "Views": ad.views,
          "Clicks": ad.clicks,
          "CTR (%)": parseFloat(ad.ctr),
        }));
        const ws = XLSX.utils.json_to_sheet(adsData);
        autoWidth(ws, adsData);
        styleHeaderRow(ws, 0, Object.keys(adsData[0] || {}).length);
        XLSX.utils.book_append_sheet(wb, ws, "Advertisements");
        XLSX.writeFile(wb, `ads-${new Date().toISOString().slice(0, 10)}.xlsx`);
      } else if (type === "daily") {
        const wb = XLSX.utils.book_new();
        const dailyData = stats.sortedDays.map((day) => ({
          Date: day,
          Views: stats.byDay[day]?.views || 0,
          Clicks: stats.byDay[day]?.clicks || 0,
          "CTR (%)":
            (stats.byDay[day]?.views || 0) > 0
              ? parseFloat((((stats.byDay[day]?.clicks || 0) / stats.byDay[day].views) * 100).toFixed(2))
              : 0,
        }));
        const ws = XLSX.utils.json_to_sheet(dailyData);
        autoWidth(ws, dailyData);
        styleHeaderRow(ws, 0, 4);
        XLSX.utils.book_append_sheet(wb, ws, "Daily Activity");
        XLSX.writeFile(wb, `daily-activity-${new Date().toISOString().slice(0, 10)}.xlsx`);
      } else if (type === "geo") {
        const wb = XLSX.utils.book_new();
        const totalGeoViews = stats.topGeo.reduce((s, [, g]) => s + g.views, 0);
        const geoData = stats.topGeo.map(([, g]) => ({
          City: g.city || "",
          State: g.state || "",
          Country: g.country || "",
          Views: g.views,
          "Share (%)":
            totalGeoViews > 0 ? parseFloat(((g.views / totalGeoViews) * 100).toFixed(1)) : 0,
        }));
        const ws = XLSX.utils.json_to_sheet(geoData);
        autoWidth(ws, geoData);
        styleHeaderRow(ws, 0, 5);
        XLSX.utils.book_append_sheet(wb, ws, "Regions");
        XLSX.writeFile(wb, `regions-${new Date().toISOString().slice(0, 10)}.xlsx`);
      }
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    getAdEvents()
      .then((res) => {
        setEvents(res || []);
      })
      .catch((err) => {
        console.error("Failed to load ad events:", err);
        setEvents([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Parse real data ──
  const stats = useMemo(() => parseAdEvents(events), [events]);

  // ── Chart data from byDay ──
  const chartData = useMemo(() => {
    if (!stats) return { labels: [], views: [], clicks: [] };
    const labels = stats.sortedDays.map((d) => {
      const date = new Date(d);
      return `${date.getDate()}.${date.getMonth() + 1}`;
    });
    const views = stats.sortedDays.map((d) => stats.byDay[d]?.views || 0);
    const clicks = stats.sortedDays.map((d) => stats.byDay[d]?.clicks || 0);
    return { labels, views, clicks };
  }, [stats]);

  // ── KPI sparklines from daily data ──
  const viewsSparkData = chartData.views;
  const clicksSparkData = chartData.clicks;

  // ── KPI cards ──
  const kpiCards = stats
    ? [
        {
          label: "Total Views",
          value: stats.totalViews.toLocaleString("ru"),
          Icon: VisibilityOutlinedIcon,
          color: "warning",
          sparkData: viewsSparkData,
        },
        {
          label: "Total Clicks",
          value: stats.totalClicks.toLocaleString("ru"),
          Icon: AdsClickOutlinedIcon,
          color: "success",
          sparkData: clicksSparkData,
        },
        {
          label: "CTR",
          value: `${stats.ctr}%`,
          Icon: PercentOutlinedIcon,
          color: "info",
          sparkData: viewsSparkData.map((v, i) =>
            v > 0 ? ((clicksSparkData[i] || 0) / v) * 100 : 0
          ),
        },
        {
          label: "Active Ads",
          value: stats.activeAds,
          Icon: CampaignOutlinedIcon,
          color: "primary",
          sparkData: null,
        },
      ]
    : [];

  return (
    <DashboardLayout>
      <ThemeProvider theme={theme}>
        <link
          href="https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&family=DM+Sans:wght@300;400;500;700&display=swap"
          rel="stylesheet"
        />

        <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: 3 }}>
          {/* ── Navbar ── */}
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              px: 2.5,
              py: 1.5,
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "rgba(255,255,255,0.85)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255,255,255,0.7)",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">Pages</Typography>
              <Typography variant="caption" color="text.secondary">/</Typography>
              <Typography variant="caption" fontWeight={700} color="text.primary">Analytics</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon />}
                onClick={handleExportClick}
                disabled={!stats || exporting}
              >
                {exporting ? "Exporting..." : "Export"}
              </Button>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleExportClose}
                PaperProps={{
                  elevation: 3,
                  sx: { mt: 1, minWidth: 240, borderRadius: 2, border: "1px solid #f0f2f5" },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem
                  onClick={() => handleExport("full")}
                  sx={{ py: 1.25, "&:hover": { bgcolor: alpha("#e91e8c", 0.06) } }}
                >
                  <ListItemIcon><TableChartOutlinedIcon fontSize="small" sx={{ color: "#e91e8c" }} /></ListItemIcon>
                  <ListItemText
                    primary="Full Report (6 sheets)"
                    secondary="Summary, Ads, Daily, Placements, Regions, Raw Events"
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={() => handleExport("ads")} sx={{ py: 1 }}>
                  <ListItemIcon><BarChartOutlinedIcon fontSize="small" sx={{ color: "#1A73E8" }} /></ListItemIcon>
                  <ListItemText
                    primary="Advertisements only"
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </MenuItem>
                <MenuItem onClick={() => handleExport("daily")} sx={{ py: 1 }}>
                  <ListItemIcon><BarChartOutlinedIcon fontSize="small" sx={{ color: "#fb8c00" }} /></ListItemIcon>
                  <ListItemText
                    primary="Daily activity only"
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </MenuItem>
                <MenuItem onClick={() => handleExport("geo")} sx={{ py: 1 }}>
                  <ListItemIcon><PlaceOutlinedIcon fontSize="small" sx={{ color: "#4CAF50" }} /></ListItemIcon>
                  <ListItemText
                    primary="Regions only"
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </MenuItem>
              </Menu>
            </Stack>
          </Paper>

          {/* ── Page Header ── */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ mb: 0.5 }}>Analytics</Typography>
              <Typography variant="body2" color="text.secondary">
                {loading
                  ? "Loading data..."
                  : stats
                  ? `Views and clicks across ${stats.sortedAds.length} advertisement${stats.sortedAds.length !== 1 ? "s" : ""}`
                  : "No data available"}
              </Typography>
            </Box>
          </Box>

          {/* ── KPI Cards ── */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {loading
              ? [1, 2, 3, 4].map((i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Card>
                      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <Box sx={{ height: 80, bgcolor: "#f5f5f5", borderRadius: 2, animation: "pulse 1.5s infinite" }} />
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              : kpiCards.map(({ label, value, Icon, color, sparkData }, i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Card>
                      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2.5,
                              background: `linear-gradient(195deg, ${alpha(theme.palette[color]?.main || "#e91e8c", 0.7)}, ${theme.palette[color]?.main || "#e91e8c"})`,
                              boxShadow: `0 4px 12px ${alpha(theme.palette[color]?.main || "#e91e8c", 0.35)}`,
                            }}
                          >
                            <Icon sx={{ fontSize: 22, color: "#fff" }} />
                          </Avatar>
                        </Box>
                        <Typography variant="h5" sx={{ mb: 0.25 }}>{value}</Typography>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        {sparkData && sparkData.length > 1 && (
                          <Box sx={{ mt: 1.5 }}>
                            <SparkLine data={sparkData} color={theme.palette[color]?.main || "#e91e8c"} />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
          </Grid>

          {/* ── Views / Clicks Trend ── */}
          <Card sx={{ mb: 2.5 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.25 }}>Views & Clicks</Typography>
                  <Typography variant="caption" color="text.secondary">
                    By day — {chartData.labels.length} day{chartData.labels.length !== 1 ? "s" : ""} of data
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  {[
                    { color: "#9c27b0", label: "Views" },
                    { color: "#4CAF50", label: "Clicks" },
                  ].map((l) => (
                    <Stack key={l.label} direction="row" spacing={0.5} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: l.color }} />
                      <Typography variant="caption" color="text.secondary">{l.label}</Typography>
                    </Stack>
                  ))}
                  <ToggleButtonGroup
                    size="small"
                    value={chartMode}
                    exclusive
                    onChange={(_, v) => v && setChartMode(v)}
                    sx={{ ml: 1 }}
                  >
                    {["views", "clicks", "both"].map((m) => (
                      <ToggleButton key={m} value={m} sx={{ fontSize: 11, px: 1.5, py: 0.5, textTransform: "none" }}>
                        {m === "views" ? "Views" : m === "clicks" ? "Clicks" : "All"}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Stack>
              </Box>
              {chartData.labels.length > 0 ? (
                <AreaChart
                  data1={chartMode !== "clicks" ? chartData.views : chartData.clicks}
                  data2={chartMode === "both" ? chartData.clicks : null}
                  labels={chartData.labels}
                  color1="#9c27b0"
                  color2="#4CAF50"
                  height={130}
                />
              ) : (
                <EmptyState label="No daily data available" />
              )}
            </CardContent>
          </Card>

          {/* ── Ads Table + Geo ── */}
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            {/* Top Ads Table */}
            <Grid item xs={12} md={8}>
              <Card>
                <Box sx={{ px: 3, pt: 2.5, pb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="h5">Top Advertisements</Typography>
                </Box>
                <Divider />
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {["Ad", "Views", "Clicks", "CTR", "Type", ""].map((h) => (
                          <TableCell key={h}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="caption" color="text.secondary">
                            Loading...
                          </Typography>
                          </TableCell>
                        </TableRow>
                      ) : stats && stats.sortedAds.length > 0 ? (
                        stats.sortedAds.map((ad, i) => {
                          const ctrNum = parseFloat(ad.ctr);
                          const ctrColor =
                            ctrNum > 5
                              ? "success"
                              : ctrNum > 2
                              ? "warning"
                              : "error";
                          return (
                            <TableRow
                              key={ad.id}
                              sx={{
                                bgcolor: i % 2 === 0 ? "#fafbfc" : "#fff",
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                                transition: "background 0.15s",
                              }}
                            >
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      bgcolor: ad.active ? "#4CAF50" : "#bbb",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    variant="subtitle2"
                                    color="text.primary"
                                    sx={{ fontSize: 13, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                  >
                                    {ad.title}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600} color={theme.palette.info.main}>
                                  {ad.views.toLocaleString("en")}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  {ad.clicks.toLocaleString("en")}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 100 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(ctrNum * 10, 100)}
                                    color={ctrColor}
                                    sx={{ flex: 1 }}
                                  />
                                  <Typography
                                    variant="caption"
                                    fontWeight={700}
                                    sx={{
                                      minWidth: 36,
                                      color: theme.palette[ctrColor].main,
                                    }}
                                  >
                                    {ad.ctr}%
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={ad.mediaType || "image"}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    bgcolor: alpha(theme.palette.info.main, 0.1),
                                    color: theme.palette.info.main,
                                    border: "none",
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton size="small" sx={{ color: "text.secondary" }}>
                                  <MoreHorizIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <EmptyState label="No advertisements" />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>

            {/* Top Geo */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 0.25 }}>Top Regions</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2.5 }}>
                    Views by city / state / country
                  </Typography>
                  {!loading && stats && stats.topGeo.length > 0 ? (
                    <Stack spacing={2.5}>
                      {stats.topGeo.map(([, g], i) => {
                        const maxCount = stats.topGeo[0][1].views;
                        const pct = Math.round((g.views / maxCount) * 100);
                        const label = [g.city, g.state, g.country].filter(Boolean).join(", ") || "Unknown";
                        return (
                          <Box key={i}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, minWidth: 14, fontWeight: 700, mt: "1px" }}>
                                  {i + 1}
                                </Typography>
                                <Box>
                                  <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                                    {g.city || g.country || "Unknown"}
                                  </Typography>
                                  {(g.state || g.country) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                                      {[g.state, g.country].filter(Boolean).join(", ")}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                                  {g.views.toLocaleString("en")}
                                </Typography>
                                <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ fontSize: 12 }}>
                                  {pct}%
                                </Typography>
                              </Stack>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={pct}
                              sx={{
                                "& .MuiLinearProgress-bar": {
                                  background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.6)}, ${theme.palette.primary.main})`,
                                  borderRadius: 4,
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <EmptyState label={loading ? "Loading..." : "No region data"} />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ── Placements ── */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 0.25 }}>Placement Breakdown</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2.5 }}>
                Views by ad placement position
              </Typography>
              {!loading && stats && stats.topPlacements.length > 0 ? (
                <Grid container spacing={2}>
                  {stats.topPlacements.map(([pl, count], i) => {
                    const total = stats.topPlacements.reduce((a, [, c]) => a + c, 0);
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    const color = PLACEMENT_COLORS[pl] || "#999";
                    return (
                      <Grid item xs={12} sm={6} md={3} key={pl}>
                        <Box sx={{ p: 2, bgcolor: alpha(color, 0.06), borderRadius: 2, border: `1px solid ${alpha(color, 0.15)}` }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                              {PLACEMENT_LABELS[pl] || pl}
                            </Typography>
                          </Stack>
                          <Typography variant="h5" sx={{ color, mb: 0.25 }}>
                            {count.toLocaleString("ru")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {pct}% of total views
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              mt: 1.5,
                              "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 4 },
                            }}
                          />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <EmptyState label={loading ? "Loading..." : "No placement data"} />
              )}
            </CardContent>
          </Card>
        </Box>
      </ThemeProvider>
    </DashboardLayout>
  );
}